/**
 * Two-factor authentication with an authenticator app (TOTP).
 *
 * Real MFA at the AUTH layer, not an app-level gate: Firebase itself refuses to
 * issue a token until the second factor is satisfied, so someone holding only
 * the password gets nothing. An app-level check would still have handed them a
 * valid session.
 *
 * NO SMS ANYWHERE, deliberately. Authenticator apps cost nothing per sign-in,
 * need no 10DLC registration, and SMS is the weaker factor (SIM swap).
 *
 * Ported from Conduit CRM, where it is in production.
 */

import {
  getMultiFactorResolver,
  multiFactor,
  sendEmailVerification,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type TotpSecret,
  type User,
} from 'firebase/auth';
import { auth } from '../firebase';

export const isVerified = (u: User | null) => !!u && !!u.emailVerified;

/**
 * Firebase refuses MFA enrolment until the address is verified, so this is a
 * hard prerequisite rather than a nicety.
 */
export async function sendVerification(): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error('Sign in first.');
  if (u.emailVerified) return;
  await sendEmailVerification(u);
}

export interface TotpEnrollment {
  secret: TotpSecret;
  /** otpauth:// URI, rendered as the QR code. */
  qrUrl: string;
  /** The same secret as text, for a phone that cannot scan its own screen. */
  manualKey: string;
}

/**
 * Begin enrolment: ask Firebase for a secret and make it scannable.
 *
 * Nothing is enrolled until finishEnrollment() succeeds with a valid code, so
 * abandoning this screen leaves the account exactly as it was.
 */
export async function beginEnrollment(issuer = 'VTG Backup Monitor'): Promise<TotpEnrollment> {
  const u = auth.currentUser;
  if (!u) throw new Error('Sign in first.');
  if (!u.emailVerified) {
    throw new Error('Verify your email address before turning on two-factor authentication.');
  }
  const session = await multiFactor(u).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    secret,
    qrUrl: secret.generateQrCodeUrl(u.email ?? 'Backup Monitor', issuer),
    manualKey: secret.secretKey,
  };
}

/** Confirm enrolment with the first code the authenticator produces. */
export async function finishEnrollment(
  secret: TotpSecret,
  code: string,
  label = 'Authenticator app'
): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error('Sign in first.');
  await multiFactor(u).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim()), label);
}

export function enrolledFactors(u: User | null): MultiFactorInfo[] {
  if (!u) return [];
  try { return multiFactor(u).enrolledFactors ?? []; } catch { return []; }
}

/**
 * Remove a factor. Firebase requires a recent sign-in, which is correct:
 * turning OFF a second factor is exactly when identity should be re-proven.
 */
export async function unenroll(factorUid: string): Promise<void> {
  const u = auth.currentUser;
  if (!u) throw new Error('Sign in first.');
  await multiFactor(u).unenroll(factorUid);
}

export const isMfaRequiredError = (e: unknown): e is MultiFactorError =>
  !!e && typeof e === 'object' && (e as { code?: string }).code === 'auth/multi-factor-auth-required';

export function mfaResolver(error: MultiFactorError): MultiFactorResolver {
  return getMultiFactorResolver(auth, error);
}

/**
 * Finish a sign-in interrupted by the second factor.
 *
 * Only TOTP factors are offered. A phone factor, if one somehow exists, is
 * skipped rather than silently falling back to SMS.
 */
export async function completeMfaSignIn(resolver: MultiFactorResolver, code: string): Promise<void> {
  const totp = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!totp) {
    throw new Error('This account has no authenticator app set up. Ask an owner to reset your two-factor login.');
  }
  await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(totp.uid, code.trim()));
}

/** Firebase's terse codes, turned into something a person can act on. */
export function mfaErrorText(e: unknown): string {
  const code = (e && typeof e === 'object' && (e as { code?: string }).code) || '';
  if (code === 'auth/invalid-verification-code') return 'That code was not accepted. Check the current 6 digits in your authenticator app.';
  if (code === 'auth/totp-challenge-timeout') return 'That took too long. Sign in again.';
  if (code === 'auth/requires-recent-login') return 'For security, sign out and back in before changing two-factor settings.';
  if (code === 'auth/unverified-email') return 'Verify your email address first.';
  if (code === 'auth/maximum-second-factor-count-exceeded') return 'This account already has the maximum number of second factors.';
  if (code === 'auth/operation-not-allowed') return 'Authenticator-app sign-in is not switched on for this project yet. An owner can enable it in Settings.';
  return e instanceof Error ? e.message : 'Something went wrong.';
}
